import { TweakPanel } from '@/commons/TweakPanel';
import { addColorControlPanel, addPositionControlPanel } from '@/commons/TweakPanelHelper';
import { throttle } from '@/utils/throttle';
import { Wedding3DWorld } from '@/littleWedding/Wedding3DWorld';
import {
    NoToneMapping,
    LinearToneMapping,
    ReinhardToneMapping,
    CineonToneMapping,
    ACESFilmicToneMapping,
    AgXToneMapping,
    NeutralToneMapping,
    CustomToneMapping,
    Object3D,
    Mesh,
    MeshStandardMaterial,
    Texture,
    ShadowMaterial,
} from 'three';
import { FolderApi, TabPageApi } from 'tweakpane';
import type { IEventMap } from '@/commons/world/typescript/IEventMap';
import type { IThrottleObserver } from '@/utils/typescript/IThrottleObserver';

const WorldDebugPanel = {
    nameSpace: {
        globalTab: 'Scene',
        meshes: 'Meshes',
    },
    worldParams: {
        toneMapping: {
            NoToneMapping,
            LinearToneMapping,
            ReinhardToneMapping,
            CineonToneMapping,
            ACESFilmicToneMapping,
            AgXToneMapping,
            NeutralToneMapping,
            CustomToneMapping,
        },
    },

    get globalTab(): TabPageApi {
        const globalTab = TweakPanel.getFolderOrTabByName(WorldDebugPanel.nameSpace.globalTab);
        if (globalTab instanceof TabPageApi) {
            return globalTab;
        }
        const tweakTabPage = TweakPanel.getNextTabApi()?.addPage({
            title: WorldDebugPanel.nameSpace.globalTab,
        });
        if (!tweakTabPage) {
            TweakPanel.getFolderOrTabByName()?.addTab({
                pages: [{ title: WorldDebugPanel.nameSpace.globalTab }],
            });
        }
        return TweakPanel.getFolderOrTabByName(WorldDebugPanel.nameSpace.globalTab) as TabPageApi;
    },

    get meshesFolder() {
        const meshesFolderExist = TweakPanel.getFolderOrTabByName(WorldDebugPanel.nameSpace.meshes);
        if (meshesFolderExist) {
            return meshesFolderExist;
        }
        return this.globalTab?.addFolder({
            title: WorldDebugPanel.nameSpace.meshes,
            expanded: false,
        });
    },

    init() {
        const { meshesFolder } = this;
        const throttleObserver: IThrottleObserver = { throttleTimer: false, object: [] };
        const afterAddedCallback = (objectAdded: IEventMap) => {
            // allow to treat all object when throttle function is triggered
            if (objectAdded.object) {
                throttleObserver.object?.push(...objectAdded.object);
            }
            throttle(
                () => {
                    Wedding3DWorld.scene?.traverse((child) => {
                        if (child.type !== 'Group' && child.type === 'Mesh') {
                            child.traverse((innerChild) => {
                                // for Meshs and Object3D
                                if (innerChild.type === 'Mesh') {
                                    WorldDebugPanel.addObject3DToControlPanel(innerChild, meshesFolder as FolderApi);
                                }
                            });
                        }
                    });
                    throttleObserver.object = [];
                },
                50,
                throttleObserver,
            )();
        };
        Wedding3DWorld.scene?.addEventListener('added', afterAddedCallback);
        this.globalTab?.addButton({ title: 'Log World' }).on('click', () => {
            console.info(Wedding3DWorld.instance);
        });
    },

    addButtons(folder: FolderApi) {
        folder?.addButton({ title: 'Log World' }).on('click', () => {
            console.info(Wedding3DWorld.instance);
        });
    },

    addObject3DToControlPanel(object: Mesh | Object3D, objectFolder: FolderApi) {
        // check if object have name
        if (!object.name) {
            object.name = `${object.type}-${object.id}`;
        }
        const checkIfObjectPanelExist = TweakPanel.getFolderOrTabByName(object.name, objectFolder);
        if (checkIfObjectPanelExist) {
            return;
        }
        const innerObjectFolder = objectFolder?.addFolder({ title: `${object.name}`, expanded: false });
        innerObjectFolder?.addBinding(object, 'visible', { label: `${object.name} Visible` });
        innerObjectFolder?.addBinding(object, 'castShadow', { label: `${object.name} CastShadow` });
        innerObjectFolder?.addBinding(object, 'receiveShadow', { label: `${object.name} ReceiveShadow` });

        const matrixFolder = innerObjectFolder?.addFolder({ title: 'Matrix', expanded: false });
        addPositionControlPanel(object, matrixFolder);
        matrixFolder.addBlade({ view: 'separator' });
        matrixFolder?.addBinding(object.rotation, 'x', { label: 'Rotation X' });
        matrixFolder?.addBinding(object.rotation, 'y', { label: 'Rotation Y' });
        matrixFolder?.addBinding(object.rotation, 'z', { label: 'Rotation Z' });
        matrixFolder.addBlade({ view: 'separator' });
        matrixFolder?.addBinding(
            {
                get scale() {
                    return object.scale.x;
                },
                set scale(scaleValue) {
                    object.scale.x = scaleValue;
                    object.scale.y = scaleValue;
                    object.scale.z = scaleValue;
                },
            },
            'scale',
            { label: 'Uniform Scale' },
        );
        if (object instanceof Mesh && object.material && object.material instanceof MeshStandardMaterial) {
            if (Array.isArray(object.material)) {
                object.material.forEach((material) => {
                    this.addMaterialToControlPanel(material, innerObjectFolder);
                });
            } else {
                this.addMaterialToControlPanel(object, innerObjectFolder);
            }
        }
        if (object instanceof Mesh && object.material && object.material instanceof ShadowMaterial) {
            const materialFolder = innerObjectFolder.addFolder({ title: `Material ${object.name}` });
            materialFolder.addBinding(
                {
                    get shadowOpacity() {
                        return (object.material as ShadowMaterial).opacity;
                    },
                    set shadowOpacity(value) {
                        (object.material as ShadowMaterial).opacity = value;
                    },
                },
                'shadowOpacity',
                { min: 0, max: 1, step: 0.01 },
            );
        }
        innerObjectFolder?.addButton({ title: `Log ${object.name}` }).on('click', () => {
            console.info(object);
        });
    },
    addMaterialToControlPanel(object: Mesh, objectFolder: FolderApi) {
        const materialFolder = objectFolder.addFolder({ title: `Material ${object.name}` });
        addColorControlPanel(object.material as MeshStandardMaterial, materialFolder);
        materialFolder.addBinding(object.material as MeshStandardMaterial, 'metalness', {
            min: 0,
            max: 1,
            step: 0.01,
        });
        materialFolder.addBinding(object.material as MeshStandardMaterial, 'roughness', {
            min: 0,
            max: 10,
            step: 0.01,
        });
        materialFolder?.addBinding(
            {
                emissiveColor: {
                    get r() {
                        return (object.material as MeshStandardMaterial).emissive.r * 255;
                    },
                    set r(value) {
                        (object.material as MeshStandardMaterial).emissive.r = value / 255;
                    },
                    get g() {
                        return (object.material as MeshStandardMaterial).emissive.g * 255;
                    },
                    set g(value) {
                        (object.material as MeshStandardMaterial).emissive.g = value / 255;
                    },
                    get b() {
                        return (object.material as MeshStandardMaterial).emissive.b * 255;
                    },
                    set b(value) {
                        (object.material as MeshStandardMaterial).emissive.b = value / 255;
                    },
                },
            },
            'emissiveColor',
            {
                label: 'Emissive Color',
                picker: 'inline',
                expanded: false,
            },
        );
        materialFolder.addBinding(object.material as MeshStandardMaterial, 'emissiveIntensity', {
            min: 0,
            max: 2,
            step: 0.001,
        });
        materialFolder.addBinding(
            {
                get normalScale() {
                    return (object.material as MeshStandardMaterial).normalScale.x;
                },
                set normalScale(value) {
                    (object.material as MeshStandardMaterial).normalScale.x = value;
                    (object.material as MeshStandardMaterial).normalScale.y = value;
                },
            },
            'normalScale',
            { min: -1, max: 1, step: 0.01 },
        );
        materialFolder.addBinding(object.material as MeshStandardMaterial, 'transparent');
        materialFolder.addBinding(object.material as MeshStandardMaterial, 'opacity', {
            min: 0,
            max: 1,
            step: 0.01,
        });
        materialFolder.addBinding(
            {
                get applyEnvMap() {
                    return !!(object.material as MeshStandardMaterial).envMap;
                },
                set applyEnvMap(value) {
                    if (value) {
                        Wedding3DWorld.environmentMap?.then((envMap: Texture) => {
                            (object.material as MeshStandardMaterial).envMap = envMap;
                        });
                    } else {
                        (object.material as MeshStandardMaterial).envMap = null;
                    }
                },
            },
            'applyEnvMap',
            { label: 'Apply EnvMap', type: 'checkbox' },
        );
        materialFolder.addBinding(object.material as MeshStandardMaterial, 'envMapIntensity', {
            min: 0,
            max: 5,
            step: 0.01,
        });
    },
};

export { WorldDebugPanel };
